import React, { Fragment, useState, useEffect, useCallback } from 'react';
import {
    Row,
    Col,
    Card,
    ListGroup,
    ListGroupItem,
    CardHeader,
    CustomInput,
    Button,
    Container,
    Modal,
    ModalHeader,
    ModalBody,
    Form,
    FormGroup,
    Label,
    CardBody
} from 'reactstrap';

import { faTrashAlt, faEdit } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import api from '../../../../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../../../../Context/Auth/AuthContext';
import { useForm } from 'react-hook-form';
import NeedStatuses from '../../../../../enums/NeedStatuses';

export default function NeedsList(props) {
    const { activeTab, refreshNeed, setRefreshNeed } = props;
    const { register, handleSubmit, reset } = useForm({});

    const [needs, setNeeds] = useState([]);
    const [noNeeds, setNoNeeds] = useState(false);
    const { toggleLoading, currentGroup } = useAuth();

    const [currentNeed, setCurrentNeed] = useState(null);
    const [modalEditNeed, setModalEditNeed] = useState(false);
    const toggleModalEditNeed = useCallback(() => {
        setModalEditNeed(!modalEditNeed);
    }, [setModalEditNeed, modalEditNeed]);

    const [modalRemoveNeed, setModalRemoveNeed] = useState(false);
    const toggleModalRemoveNeed = useCallback(() => {
        setModalRemoveNeed(!modalRemoveNeed);
    }, [setModalRemoveNeed, modalRemoveNeed]);

    const [lastId, setLastId] = useState(null);

    const getNeeds = useCallback(() => {
        if (JSON.parse(activeTab) === 7) {
            toggleLoading(true);
            api.post(`/ro.team.listallNeeds/${currentGroup.id}`)
                .then((response) => {
                    setNeeds(response.data.items);
                    setNoNeeds(response.data.items.length === 0);
                    setLastId(response.data.lastId);
                })
                .catch((err) => {
                    toast.error(err.response.data.message);
                })
                .finally(() => {
                    toggleLoading(false);
                });
        }
    }, [toggleLoading, currentGroup, setNeeds, activeTab, setNoNeeds, setLastId]);

    const loadMore = useCallback(() => {
        toggleLoading(true);
        api.post(`/ro.team.listallNeeds/${currentGroup.id}`, {lastId})
            .then((response) => {
                setNeeds([...needs, ...response.data.items]);
                setLastId(response.data.lastId);
            })
            .catch((err) => {
                toast.error(err.response.data.message);
            })
            .finally(() => {
                toggleLoading(false);
            });
    }, [toggleLoading, currentGroup, lastId, needs, setNeeds]);

    useEffect(() => {
        if (refreshNeed) {
            setRefreshNeed(false);
            getNeeds();
        } else {
            getNeeds();
        }    
    }, [getNeeds, refreshNeed, setRefreshNeed]);

    const onSubmitEditNeed = useCallback(
        async (data) => {
            toggleLoading(true);
            api.patch(`/ro.team.modifyNeed/${currentNeed.id}`, {
                gid: currentGroup.id,
                receiverId: currentNeed.receiver.id,
                content: data.need
            })
                .then((response) => {
                    toast.success(`Need edited`);
                    toggleModalEditNeed();
                    reset(response);
                    getNeeds();
                })
                .catch((err) => {
                    toast.error(err.response.data.message);
                })
                .finally(() => {
                    toggleLoading(false);
                });
        },
        [toggleModalEditNeed, reset, currentGroup.id, toggleLoading, currentNeed, getNeeds]
    );

    const onSubmitRemoveNeed = useCallback(
        async () => {
            toggleLoading(true);
            api.delete(`/ro.team.deleteNeed/${currentNeed.id}`, {
                data: {
                    gid: currentGroup.id,
                    receiverId: currentNeed.receiver.id
                }
            })
                .then((response) => {
                    toast.success(`Need removed`);
                    toggleModalRemoveNeed();
                    reset(response);
                    getNeeds();
                })
                .catch((err) => {
                    toast.error(err.response.data.message);
                })
                .finally(() => {
                    toggleLoading(false);
                });
        },
        [toggleModalRemoveNeed, reset, currentGroup.id, toggleLoading, currentNeed, getNeeds]
    );

    const completeNeed = useCallback((need) => {
        toggleLoading(true);
        api.patch(`/ro.team.completeNeed/${need.id}`, {
            gid: currentGroup.id,
            receiverId: need.receiver.id
        })
            .then(() => {
                toast.success(`Need completed`);
                getNeeds();
            })
            .catch((err) => {
                toast.error(err.response.data.message);
            })
            .finally(() => {
                toggleLoading(false);
            });
    }, [toggleLoading, currentGroup, getNeeds]);

    const uncompleteNeed = useCallback((need) => {
        toggleLoading(true);
        api.patch(`/ro.team.uncompleteNeed/${need.id}`, {
            gid: currentGroup.id,
            receiverId: need.receiver.id
        })
            .then(() => {
                toast.success(`Need marked as not completed`);
                getNeeds();
            })
            .catch((err) => {
                toast.error(err.response.data.message);
            })
            .finally(() => {
                toggleLoading(false);
            });
    }, [toggleLoading, currentGroup, getNeeds]);

    return (
        <Fragment>
            <Container fluid>
                <Row>
                    <Col lg="12">
                        <Card className="main-card mb-3">
                            <CardHeader>Senior Needs</CardHeader>
                            <ListGroup className="todo-list-wrapper" flush>
                                {needs &&
                                    needs.map((need) => (
                                        <ListGroupItem key={need.id}>
                                            <div className="widget-content p-0">
                                                <div className="widget-content-wrapper">
                                                    <div className="widget-content-left mr-2">
                                                        <CustomInput
                                                            type="checkbox"
                                                            id={`need-${need.id}`}
                                                            label="&nbsp;"
                                                            checked={need.status === NeedStatuses.COMPLETED}
                                                            // disabled={need.status === NeedStatuses.COMPLETED}
                                                            onChange={() => {
                                                                if (need.status === NeedStatuses.COMPLETED) {
                                                                    uncompleteNeed(need);
                                                                } else {
                                                                    completeNeed(need)
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="widget-content-left">
                                                        <div className="widget-heading">
                                                            {need.content}
                                                        </div>
                                                        <div className="widget-subheading">
                                                            Senior: {need.receiver.name}
                                                        </div>
                                                    </div>
                                                    <div className="widget-content-right widget-content-actions">
                                                        <Button
                                                            className="border-0 btn-transition"
                                                            outline
                                                            color="info"
                                                            onClick={() => {
                                                                setCurrentNeed(need);
                                                                toggleModalEditNeed();
                                                            }}
                                                            // disabled={need.status === NeedStatuses.COMPLETED}
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faEdit}
                                                            />
                                                        </Button>
                                                        <Button
                                                            className="border-0 btn-transition"
                                                            outline
                                                            color="danger"
                                                            onClick={() => {
                                                                setCurrentNeed(need);
                                                                toggleModalRemoveNeed();
                                                            }}
                                                            // disabled={need.status === NeedStatuses.COMPLETED}
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={
                                                                    faTrashAlt
                                                                }
                                                            />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        </ListGroupItem>
                                    ))}
                            </ListGroup>
                            {noNeeds && (
                                <CardBody>
                                    <p>The Seniors do no have any reported need or you do not have assigned Seniors yet</p>
                                </CardBody>
                            )}
                        </Card>

                        {lastId !== null && (
                            <div className="text-center">
                                <Button
                                    className="btn-wide mb-2 mr-2"
                                    size="md"
                                    color="info"
                                    onClick={loadMore}
                                >
                                    Load more
                                </Button>
                            </div>
                        )}

                    </Col>
                </Row>

                <Modal isOpen={modalEditNeed} toggle={toggleModalEditNeed}>
                    <ModalHeader toggle={toggleModalEditNeed}>
                        Edit Need
                    </ModalHeader>
                    <ModalBody>
                        <Form>
                            <FormGroup row>
                                <Label sm={2}>
                                    Senior
                                </Label>
                                <Col sm={10}>
                                    <input
                                        type="text"
                                        className="form-control"
                                        disabled={true}
                                        value={currentNeed && currentNeed.receiver.name}
                                    />
                                </Col>
                            </FormGroup>
                            <FormGroup row>
                                <Label for="need" sm={2}>
                                    Need
                                </Label>
                                <Col sm={10}>
                                    <input
                                        type="text"
                                        name="need"
                                        id="need"
                                        ref={register}
                                        className="form-control"
                                        defaultValue={currentNeed && currentNeed.content}
                                    />
                                </Col>
                            </FormGroup>
                            <div className="text-center">
                                <Button
                                    className="btn-wide mb-2 mr-2"
                                    size="md"
                                    color="success"
                                    onClick={handleSubmit(onSubmitEditNeed)}
                                >
                                    Ok
                                </Button>

                                <Button
                                    className="btn-wide mb-2 mr-2"
                                    size="md"
                                    color="primary"
                                    onClick={toggleModalEditNeed}
                                >
                                    Cancel
                                </Button>

                                <Button
                                    className="btn-wide mb-2 mr-2"
                                    size="md"
                                    color="danger"
                                    onClick={() => {
                                        toggleModalEditNeed();
                                        toggleModalRemoveNeed();
                                    }}
                                >
                                    Delete
                                </Button>
                            </div>
                        </Form>
                    </ModalBody>
                </Modal>

                <Modal isOpen={modalRemoveNeed} toggle={toggleModalRemoveNeed}>
                    <ModalHeader toggle={toggleModalRemoveNeed}>
                        Confirm Remove Need
                    </ModalHeader>
                    <ModalBody>
                        <Form>
                            <FormGroup row>
                                <Label for="need" sm={2}>
                                    Need
                                </Label>
                                <Col sm={10}>
                                    <input
                                        type="text"
                                        name="need"
                                        id="need"
                                        className="form-control"
                                        defaultValue={currentNeed && currentNeed.content}
                                        disabled={true}
                                    />
                                </Col>
                            </FormGroup>
                            <div className="text-center">
                                <Button
                                    className="btn-wide mb-2 mr-2"
                                    size="md"
                                    color="danger"
                                    onClick={onSubmitRemoveNeed}
                                >
                                    Remove
                                </Button>

                                <Button
                                    className="btn-wide mb-2 mr-2"
                                    size="md"
                                    color="primary"
                                    onClick={toggleModalRemoveNeed}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </Form>
                    </ModalBody>
                </Modal>

            </Container>
        </Fragment>
    );
}
